import { getApiUrl } from "../config/apiConfig";

// Location endpoints
export const ENDPOINTS = {
  // Countries
  countries: {
    base: () => getApiUrl("/countries"),
    byId: (id: string) => getApiUrl(`/countries/${id}`),
  },
  // Regions
  regions: {
    base: () => getApiUrl("/regions"),
    byId: (id: string) => getApiUrl(`/regions/${id}`),
    create: () => getApiUrl("/regions"),
  },
  // Cities
  cities: {
    base: () => getApiUrl("/cities"),
    byId: (id: string) => getApiUrl(`/cities/${id}`),
    create: () => getApiUrl("/cities"),
    byRegion: (regionId: string) => getApiUrl(`/cities/region/${regionId}`),
  },
  // Corregimientos
  corregimientos: {
    base: () => getApiUrl("/corregimientos"),
    byId: (id: string) => getApiUrl(`/corregimientos/${id}`),
    create: () => getApiUrl("/corregimientos"),
  },
  // Veredas
  veredas: {
    base: () => getApiUrl("/veredas"),
    byId: (id: string) => getApiUrl(`/veredas/${id}`),
    create: () => getApiUrl("/veredas"),
    byCity: (cityId: string) => getApiUrl(`/veredas/city/${cityId}`),
  },
  // Organizations
  organizations: {
    base: () => getApiUrl("/organizations"),
    parents: () => getApiUrl("/organizations/parents"), // Endpoint para obtener organizaciones padre
    own: () => getApiUrl("/organizations/own"),
    farms: () => getApiUrl("/organizations/farms"),
    search: () => getApiUrl("/organizations/search"),
    details: (id: string) => getApiUrl(`/organizations/details/${id}`),
    addOrganizationsToGroup: (ownOrganizationId: string, groupId: string) =>
      getApiUrl(`/organizations/${ownOrganizationId}/groups/${groupId}/organizations`),
    groups: (id: string) => getApiUrl(`/organizations/${id}/groups/organizations`),
    batchCreate: (parentOrganizationId: string) =>
      getApiUrl(`/organizations/${parentOrganizationId}/batch`),
    checkExisting: (parentOrganizationId: string) =>
      getApiUrl(`/organizations/${parentOrganizationId}/check-existing`),
    batchUpsert: (parentOrganizationId: string) =>
      getApiUrl(`/organizations/${parentOrganizationId}/batch-upsert`),
    batchDelete: (parentOrganizationId: string) =>
      getApiUrl(`/organizations/${parentOrganizationId}/batch-delete`),
    export: (parentOrganizationId: string) =>
      getApiUrl(`/organizations/${parentOrganizationId}/export`),
    adminAll: () => getApiUrl("/organizations/admin/all"),
    adminCreate: () => getApiUrl("/organizations/admin"),
    adminUpdate: (id: string) => getApiUrl(`/organizations/admin/${id}`),
    adminDelete: (id: string) => getApiUrl(`/organizations/admin/${id}`),
  },
  // Groups
  groups: {
    base: (organizationId: string) => getApiUrl(`/organizations/${organizationId}/groups`),
    byId: (organizationId: string, id: string) =>
      getApiUrl(`/organizations/${organizationId}/groups/${id}`),
  },
  // Groups Organizations (header-based tenant context - X-Organization-Id)
  // Migration: organizationId removed from URL paths. Organization context comes from header.
  groupsOrganizations: {
    /**
     * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
     */
    list: (_organizationId: string, groupId: string) =>
      getApiUrl(`/groups/${groupId}/organizations`),
    /**
     * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
     */
    createBatchRelations: (_organizationId: string, groupId: string) =>
      getApiUrl(`/groups/${groupId}/organizations`),
    /**
     * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
     */
    deleteRelation: (_organizationId: string, groupId: string, groupOrganizationId: string) =>
      getApiUrl(`/groups/${groupId}/organizations/${groupOrganizationId}`),
  },
  // Users
  users: {
    base: () => getApiUrl("/users"),
    byId: (id: string) => getApiUrl(`/users/${id}`),
    create: () => getApiUrl("/users"),
    update: (id: string) => getApiUrl(`/users/${id}`),
    adminCreate: () => getApiUrl("/users/admin"),
    adminUpdate: (id: string) => getApiUrl(`/users/admin/${id}`),
    adminDelete: (id: string) => getApiUrl(`/users/admin/${id}`),
  },
  // Roles
  roles: {
    base: () => getApiUrl("/roles"),
    byId: (id: string) => getApiUrl(`/roles/${id}`),
    create: () => getApiUrl("/roles"),
    byOrganization: (organizationId: string) => getApiUrl(`/roles/organization/${organizationId}`),
  },
  // Privileges
  privileges: {
    base: () => getApiUrl("/privileges"),
    byId: (id: string) => getApiUrl(`/privileges/${id}`),
  },
  // User Roles and Privileges (header-based tenant context - X-Organization-Id)
  // Migration: organizationId removed from URL paths. Organization context comes from header.
  userRoles: {
    /**
     * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
     */
    base: (_organizationId: string, userId: string) => getApiUrl(`/users/${userId}/roles`),
    /**
     * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
     */
    byUser: (_organizationId: string, userId: string) => getApiUrl(`/users/${userId}/roles`),
    /**
     * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
     */
    byUserDetailed: (_organizationId: string, userId: string) =>
      getApiUrl(`/users/${userId}/roles/detailed`),
    /**
     * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
     */
    byRoleId: (_organizationId: string, userId: string, roleId: string) =>
      getApiUrl(`/users/${userId}/roles/${roleId}`),
    /**
     * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
     */
    byRoleIdDetailed: (_organizationId: string, userId: string, roleId: string) =>
      getApiUrl(`/users/${userId}/roles/${roleId}/detailed`),
    /**
     * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
     */
    create: (_organizationId: string, userId: string) => getApiUrl(`/users/${userId}/roles`),
    /**
     * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
     */
    update: (_organizationId: string, userId: string, id: string) =>
      getApiUrl(`/users/${userId}/roles/${id}`),
    /**
     * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
     */
    delete: (_organizationId: string, userId: string, id: string) =>
      getApiUrl(`/users/${userId}/roles/${id}`),
    /**
     * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
     */
    own: (_organizationId: string, userId: string) => getApiUrl(`/users/${userId}/roles/own`),
    /**
     * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
     */
    ownDetailed: (_organizationId: string, userId: string) =>
      getApiUrl(`/users/${userId}/roles/own/detailed`),
  },
  rolePrivileges: {
    base: () => getApiUrl("/roles/privileges"),
    byId: (id: string) => getApiUrl(`/roles/privileges/${id}`),
    create: () => getApiUrl("/roles/privileges"),
    own: (roleId: string) => getApiUrl(`/roles/privileges/own/${roleId}`),
    ownDetailed: (roleId: string) => getApiUrl(`/roles/privileges/own/${roleId}/detailed`),
    tiers: (roleId: string) => getApiUrl(`/roles/privileges/${roleId}/tiers`),
  },
  // Farm Locations
  farmLocations: {
    base: (organizationId: string) => getApiUrl(`/organizations/${organizationId}/locations`),
    byId: (organizationId: string, id: string) =>
      getApiUrl(`/organizations/${organizationId}/locations/${id}`),
    create: (organizationId: string) => getApiUrl(`/organizations/${organizationId}/locations`),
    update: (organizationId: string, id: string) =>
      getApiUrl(`/organizations/${organizationId}/locations/${id}`),
  },
  // Farm Socioeconomics
  farmSocioeconomics: {
    base: (organizationId: string) => getApiUrl(`/organizations/${organizationId}/socioeconomics`),
    byId: (organizationId: string, id: string) =>
      getApiUrl(`/organizations/${organizationId}/socioeconomics/${id}`),
    create: (organizationId: string) =>
      getApiUrl(`/organizations/${organizationId}/socioeconomics`),
    update: (organizationId: string, id: string) =>
      getApiUrl(`/organizations/${organizationId}/socioeconomics/${id}`),
  },
  // Organization Users
  organizationUsers: {
    base: () => getApiUrl("/organization-users"),
    byId: (id: string) => getApiUrl(`/organization-users/${id}`),
    addUserToOrganization: (organizationId: string) =>
      getApiUrl(`/organizations/${organizationId}/users`),
    addUserToOrganizationAdmin: (organizationId: string) =>
      getApiUrl(`/organizations/${organizationId}/users/admin`),
    updateUserInOrganization: (organizationId: string, id: string) =>
      getApiUrl(`/organizations/${organizationId}/users/${id}`),
  },
  // Organization Dynamic Properties
  organizationDynamicProperties: {
    base: () => getApiUrl("/organizationdynamicproperties"),
    byId: (id: string) => getApiUrl(`/organizationdynamicproperties/${id}`),
    byOrganization: (organizationId: string) =>
      getApiUrl(`/organizationdynamicproperties/organizations/${organizationId}`),
  },
  // ID Types
  idTypes: {
    base: () => getApiUrl("/idTypes"),
    byId: (id: string) => getApiUrl(`/idTypes/${id}`),
  },
  // Findings (header-based tenant context - X-Organization-Id)
  // Migration: organizationId removed from URL paths. Organization context comes from header.
  findings: {
    /**
     * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
     */
    base: (_organizationId: string, inspectionId: string) =>
      getApiUrl(`/inspections/${inspectionId}/findings`),
    /**
     * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
     */
    byId: (_organizationId: string, inspectionId: string, id: string) =>
      getApiUrl(`/inspections/${inspectionId}/findings/${id}`),
    files: {
      /**
       * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
       */
      base: (_organizationId: string, inspectionId: string, findingId: string) =>
        getApiUrl(`/inspections/${inspectionId}/findings/${findingId}/files`),
      /**
       * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
       */
      byId: (_organizationId: string, inspectionId: string, findingId: string, id: string) =>
        getApiUrl(`/inspections/${inspectionId}/findings/${findingId}/files/${id}`),
    },
    items: {
      /**
       * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
       */
      base: (_organizationId: string, inspectionId: string, findingId: string) =>
        getApiUrl(`/inspections/${inspectionId}/findings/${findingId}/items`),
      /**
       * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
       */
      byId: (_organizationId: string, inspectionId: string, findingId: string, id: string) =>
        getApiUrl(`/inspections/${inspectionId}/findings/${findingId}/items/${id}`),
    },
  },
  // Program Version Items
  programVersionItems: {
    base: (organizationId: string, programId: string, versionId: string) =>
      getApiUrl(
        `/organizations/${organizationId}/programs/${programId}/versions/${versionId}/items`
      ),
    byId: (organizationId: string, programId: string, versionId: string, id: string) =>
      getApiUrl(
        `/organizations/${organizationId}/programs/${programId}/versions/${versionId}/items/${id}`
      ),
  },

  admin: {
    roles: () => getApiUrl("/admin/roles"),
    userRoles: (userId: string) => getApiUrl(`/admin/users/${userId}`),
    userRolesAsAdmin: {
      create: (userId: string) => getApiUrl(`/users/${userId}/roles/as-admin`),
      delete: (userId: string, id: string) => getApiUrl(`/users/${userId}/roles/as-admin/${id}`),
    },
  },
};
